import React from 'react'
import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { withOptimizely } from '@optimizely/react-sdk'
import { differenceInYears } from 'date-fns'
import { pathOr } from 'ramda'
import { branch, compose, defaultProps, renderComponent, withHandlers, withProps } from 'recompose'

import IconImage from 'assets/images/recommendations.png'
import Button from 'components/Button'
import CenteredSpinner from 'components/CenteredSpinner'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { withEnv } from 'utils/environment'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'

import { TestIDs } from '../../test/utils/testIds'
import { Container, Image, Text, Title } from './styled-components'
import userStateInfo from './userState.query.gql'

type Props = {
  handleClick: () => void
  header: string
  text: string
  loading: boolean
} & RemoteStringsProps

const NextButton = withClickTrack<any, any>(TRACKER_IDS.SUITABILITY_INTERSTITIAL.NEXT_BUTTON)(
  Button,
)

enum Action {
  HasLaterAccount,
  HasPortfolioButIneligible,
  Other,
}

const determineAction = (data, resumeEnabled) => {
  if (!resumeEnabled) {
    return Action.Other
  }

  const hasLaterAccount = pathOr(null, ['laterAccounts', 0], data)

  if (hasLaterAccount) {
    return Action.HasLaterAccount
  }

  const portfolioId = pathOr(null, ['user', 'portfolio', 'id'], data)
  const dob = pathOr(null, ['user', 'dob'], data)
  const age = differenceInYears(new Date(), new Date(dob))

  if (portfolioId && age >= 69) {
    return Action.HasPortfolioButIneligible
  } else {
    return Action.Other
  }
}

export const enhance = compose<Props, {}>(
  withEnv,
  defaultProps({ loading: true }),
  withRouter,
  withFlowRouter,
  withRemoteStrings,
  withOptimizely,
  graphql(userStateInfo, { options: { fetchPolicy: 'network-only' } }),
  withProps(({ getRemoteString, data, route, env }) => {
    if (data.loading) {
      return
    }

    const resumeEnabled = env.get('resumeEnabled') === 'true'
    const result = determineAction(data, resumeEnabled)
    if (result === Action.HasLaterAccount || result === Action.HasPortfolioButIneligible) {
      route({ from: FlowRoute.SuitabilityInterstitial, action: 'skip', doNotTrackVisited: true })
    } else {
      return {
        loading: data.loading,
        header: getRemoteString('suitability_interstitial_header', 'Now, get your recommendations'),
        text: getRemoteString(
          'suitability_interstitial_text',
          'Just answer a few quick questions so we can recommend the best ways to reach your retirement goals.',
        ),
      }
    }
  }),
  withHandlers({
    handleClick: ({ route }) => () => {
      route({ from: FlowRoute.SuitabilityInterstitial, action: 'continue' })
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)

export const SuitabilityInterstitial: React.SFC<Props> = ({ handleClick, header, text }) => (
  <>
    <Nav />
    <Layout>
      <Container>
        <Image src={IconImage} data-testid={TestIDs.SUITABILITY_INTERSTITIAL.IMAGE} />
        <Title data-testid={TestIDs.SUITABILITY_INTERSTITIAL.HEADER}>{header}</Title>
        <Text data-testid={TestIDs.SUITABILITY_INTERSTITIAL.TEXT}>{text}</Text>
        <Separator />
        <NextButton onPress={handleClick} data-testid={TestIDs.SUITABILITY_INTERSTITIAL.SUBMIT_CTA}>
          Next
        </NextButton>
      </Container>
    </Layout>
  </>
)

export default enhance(SuitabilityInterstitial)
